import { Reveal, Overline } from "../components/Motion";
import { Process, ServiceAreas, EstimateSection, WhyUs } from "../components/Sections";
import { PageHero } from "./ServicePage";
import { RealPhotos } from "../components/RealPhotos";
import { BIZ, IMG } from "../data/site";
import { Seo } from "../components/Seo";

const VALUES = [
  { t: "Honest advice", d: "We tell you what your home actually needs — not what makes the biggest invoice." },
  { t: "Quality materials", d: "We use materials made to handle Wisconsin snow, ice, wind and hail." },
  { t: "Respect for your home", d: "Tarps down, landscaping protected, and a full clean-up when we're done." },
  { t: "Clear communication", d: "You'll always know what's happening, when and why." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <Seo title="About Good Home Improvement LLC | Milwaukee Roofing Contractor" description="Locally owned Milwaukee roofing, siding and gutter contractor. BBB Accredited (A rating), GAF Certified, WI license 1452-DCFR, 7+ years experience. 414-396-7510." path="/about" />
      <PageHero img={IMG.crewTeam} crumb="About" kicker="About us" lines={["Meet", <span key="a" className="text-amber-400">Good Home.</span>]} sub="A locally owned Milwaukee team for roofing, siding and gutters." testid="about-hero" />
      <section className="bg-white py-24 md:py-32">
        <div className="wrap grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <Overline>Our story</Overline>
            <h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">A local team that does the outside of your home right</h2>
            <p className="mt-6 text-slate-600">{BIZ.name} is a locally owned Milwaukee exterior contractor serving homeowners across southeastern Wisconsin. We're BBB Accredited with an A rating, licensed in Wisconsin ({BIZ.license}), and proud of our five-star reviews.</p>
            <p className="mt-4 text-slate-600">Our crew brings {BIZ.experience} years of hands-on experience to every job. We install GAF Timberline HDZ shingles, seamless metal gutters and vinyl siding, offer free drone roof inspections, and help homeowners through hail and storm insurance claims. We focus on the details other companies skip — every shingle aligned, every gutter shedding water exactly where it should.</p>
            <dl className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 text-sm" data-testid="company-facts">
              {[["Company", "Good Home Improvement LLC"], ["Founded", BIZ.founded], ["BBB Accredited", `Since ${BIZ.bbbSince} · A rating`], ["GAF", `Certified™ · ID ${BIZ.gafId}`], ["WI License", "1452-DCFR"], ["Based in", "Milwaukee, WI"], ["Estimates", "Always free"]].map(([k, v]) => (
                <div key={k} className="bg-white p-4"><dt className="text-xs font-bold uppercase tracking-widest text-slate-500">{k}</dt><dd className="mt-1 font-semibold text-slate-900">{v}</dd></div>
              ))}
            </dl>
          </Reveal>
          <Reveal className="lg:col-span-6 lg:sticky lg:top-28 lg:self-start" delay={0.1}>
            <div className="grid grid-cols-2 gap-4">
              <img loading="lazy" decoding="async" src="/img/fb/crew-lineup.jpg" alt="The Good Home Improvement crew" className="aspect-[3/4] w-full rounded-2xl object-cover" />
              <img loading="lazy" decoding="async" src="/img/fb/finished-home.jpg" alt="A finished Good Home Improvement project" className="mt-12 aspect-[3/4] w-full rounded-2xl object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
      <section className="bg-slate-950 py-16 text-white" data-testid="about-stats">
        <div className="wrap grid grid-cols-2 gap-y-10 md:grid-cols-4">
          {[[`${BIZ.experience}`, "Years experience"], ["A", "BBB rating"], ["5.0★", "Google reviews"], ["GAF", "Certified™ contractor"]].map(([b, s], i) => (
            <Reveal key={s} delay={i * 0.07}><span className="font-display block text-6xl font-black leading-none md:text-7xl">{b}</span><span className="mt-2 block text-xs font-bold uppercase tracking-widest text-slate-400">{s}</span></Reveal>
          ))}
        </div>
      </section>
      <section className="bg-slate-50 py-24" data-testid="values-section">
        <div className="wrap">
          <Reveal><Overline>What we stand for</Overline><h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">Our promise to you</h2></Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.07}>
                <div className="h-full rounded-2xl border-t-4 border-slate-900 bg-white p-7 shadow-sm">
                  <h3 className="font-display text-2xl font-bold uppercase text-slate-900">{v.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <RealPhotos limit={9} />
      <WhyUs img={IMG.crewHero} />
      <Process />
      <ServiceAreas />
      <EstimateSection />
    </main>
  );
}
